import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { playStoreUrl, trackEvent } from './analytics';
import Seo from './Seo';
import Breadcrumbs, { buildBreadcrumbSchema } from './Breadcrumbs';
import './App.css';
import './HowItWorks.css';
import Footer from './Footer';
import Navbar from './Navbar';

const breadcrumbItems = [
  { name: 'Home', path: '/' },
  { name: 'How it Works', path: '/how-it-works' },
];

const faqs = [
  {
    q: 'How does Baatcheet match me with someone?',
    a: 'Pick a mood — chill, vent, hustle, or random — and Baatcheet\'s matching system pairs you with a real, verified person on the same wavelength, usually in under 8 seconds.',
  },
  {
    q: 'Is Baatcheet anonymous?',
    a: 'Yes. Every call starts anonymous by default — no number, face, or name required. You choose if and when to reveal your identity.',
  },
  {
    q: 'Do I need to create a detailed profile to use Baatcheet?',
    a: 'No. Sign in with OTP in under 30 seconds, set your mood, and you\'re ready to talk. No lengthy forms.',
  },
  {
    q: 'Are the people on Baatcheet verified?',
    a: 'Yes. Baatcheet uses identity verification so you\'re always talking to real, verified humans — not bots or fake accounts.',
  },
];

const howItWorksJsonLd = [
  buildBreadcrumbSchema(breadcrumbItems),
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(f => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  },
];

function HowItWorks() {
  useEffect(() => {
    const io = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
      }),
      { threshold: 0.12 }
    );
    document.querySelectorAll('.reveal').forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <>
      <Seo
        title="How Baatcheet Works — Match & Talk in 3 Steps | Baatcheet"
        description="See how Baatcheet matches you with real people in under 8 seconds. Pick your mood, connect over audio or video, and start talking."
        path="/how-it-works"
        jsonLd={howItWorksJsonLd}
      />
      <div className="bg" />
      <div className="confetti">
        <i className="dot s1" /><i className="cbar s2" /><i className="dot s3" /><i className="cbar s4" />
        <i className="dot s5" /><i className="dot s6" /><i className="cbar s7" /><i className="dot s8" />
        <i className="cbar s9" /><i className="dot s10" />
      </div>

      {/* ── Header ── */}
      <Navbar />
      <Breadcrumbs items={breadcrumbItems} />

      <main>

        {/* ── HERO ── */}
        <section className="hiw-hero shell">
          <div className="hiw-hero-grid">
            <div className="hiw-hero-copy reveal">
              <span className="hiw-kicker">✦ Just Three Steps</span>
              <h1 className="hiw-title">
                Three Steps,<br />
                And You're<br />
                <span className="hiw-h-stamp">Connected!</span>
              </h1>
              <p className="hiw-sub">
                Mood chunno, match pao, baat karo. Baatcheet ke saath connection
                itna aasaan kab tha? No forms, no fake profiles — sirf asli log, asli baat.
              </p>
              <div className="hiw-chips">
                <span className="hiw-chip">
                  <span className="hiw-chip-n">1</span> Vibe Chunno
                </span>
                <span className="hiw-chip">
                  <span className="hiw-chip-n">2</span> Connect Karo
                </span>
                <span className="hiw-chip hiw-chip-hi">
                  <span className="hiw-chip-n">3</span> Baat Karo!
                </span>
              </div>
            </div>
            <div className="hiw-hero-img reveal">
              <img src="/how-hero.webp" alt="How Baatcheet Works — mood-matched voice call illustration" width="1402" height="1122" fetchpriority="high" />
            </div>
          </div>
        </section>

        {/* ── STRIP ── */}
        <div className="stripe">
          <div className="stripe-track">
            <span>Pick a Vibe</span><span>8 sec match</span>
            <span>Anonymous calls</span><span>Real humans only</span>
            <span>HD audio</span><span>Made in Bharat</span>
            <span>Pick a Vibe</span><span>8 sec match</span>
            <span>Anonymous calls</span><span>Real humans only</span>
            <span>HD audio</span><span>Made in Bharat</span>
          </div>
        </div>

        {/* ── STEPS ── */}
        <section className="hiw-steps shell">

          {/* Step 1 */}
          <div className="hiw-step reveal">
            <div className="hiw-step-bg-num">01</div>
            <div className="hiw-step-inner">
              <div className="hiw-step-text">
                <span className="hiw-step-tag">Step 1</span>
                <h2 className="hiw-step-h">
                  Apna Mood<br />
                  <span className="hiw-col-cyan">Choose Karo</span>
                </h2>
                <p className="hiw-step-p">
                  Chill karna hai, vent karna hai, ya kisi se bilkul naya connection
                  banana hai? Baatcheet tumhara vibe samajhta hai — OTP se login karo
                  aur apna mood set karo. 8 seconds mein tumhara perfect match tayaar.
                </p>
                <ul className="hiw-list">
                  <li><span className="hiw-li-icon">🔐</span> OTP login — 30 seconds mein ready</li>
                  <li><span className="hiw-li-icon">🎭</span> Moods: Chill · Vent · Hustle · Random</li>
                  <li><span className="hiw-li-icon">🙈</span> Anonymous by default — naam reveal optional</li>
                </ul>
              </div>
              <div className="hiw-step-img-wrap">
                <img className="hiw-step-img" src="/how-app-dark.webp" alt="Baatcheet mood call screen" width="1349" height="1166" loading="lazy" decoding="async" />
                <div className="hiw-sticky hiw-sticky-1">8 sec<br />match! ⚡</div>
              </div>
            </div>
          </div>

          {/* Connector */}
          <div className="hiw-conn">
            <div className="hiw-conn-dash" />
            <div className="hiw-conn-badge">✦</div>
            <div className="hiw-conn-dash" />
          </div>

          {/* Step 2 */}
          <div className="hiw-step reveal">
            <div className="hiw-step-bg-num">02</div>
            <div className="hiw-step-inner hiw-step-flip">
              <div className="hiw-step-img-wrap">
                <img className="hiw-step-img" src="/how-call-screen.webp" alt="Baatcheet call screen" width="1536" height="1024" loading="lazy" decoding="async" />
                <div className="hiw-sticky hiw-sticky-2">Crystal clear<br />audio 🎙️</div>
              </div>
              <div className="hiw-step-text">
                <span className="hiw-step-tag">Step 2</span>
                <h2 className="hiw-step-h">
                  Ek Tap,<br />
                  <span className="hiw-col-rose">Baat Shuru!</span>
                </h2>
                <p className="hiw-step-p">
                  Baatcheet ka smart algorithm 8 seconds mein tumhara perfect match dhundta hai.
                  Audio call, video call, ya voice note — jaise dil chahe vaise karo. Face reveal?
                  Bilkul tumhari marzi.
                </p>
                <ul className="hiw-list">
                  <li><span className="hiw-li-icon">📞</span> Crystal clear HD audio calls</li>
                  <li><span className="hiw-li-icon">📹</span> Video call — jab aur jab tak chaaho</li>
                  <li><span className="hiw-li-icon">🎤</span> Voice notes — type karne ki zaroorat nahi</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Connector */}
          <div className="hiw-conn">
            <div className="hiw-conn-dash" />
            <div className="hiw-conn-badge">✦</div>
            <div className="hiw-conn-dash" />
          </div>

          {/* Step 3 */}
          <div className="hiw-step reveal">
            <div className="hiw-step-bg-num">03</div>
            <div className="hiw-step-inner">
              <div className="hiw-step-text">
                <span className="hiw-step-tag">Step 3</span>
                <h2 className="hiw-step-h">
                  Asli Log,<br />
                  <span className="hiw-col-gold">Asli Dosti</span>
                </h2>
                <p className="hiw-step-p">
                  Baatcheet mein sirf verified, genuine log hain — bots nahi, fake accounts nahi.
                  Yahan har baat seedhi dil se aati hai. Ek conversation se shuru hoti hai asli dosti.
                  Jaanna hai hum safety kaise ensure karte hain? <Link to="/safety">Yahan dekho</Link>.
                </p>
                <ul className="hiw-list">
                  <li><span className="hiw-li-icon">✅</span> 100% verified real humans</li>
                  <li><span className="hiw-li-icon">🔒</span> End-to-end private conversations</li>
                  <li><span className="hiw-li-icon">❤️</span> Yahan sab apne hain</li>
                </ul>
              </div>
              <div className="hiw-step-img-wrap">
                <img className="hiw-step-img hiw-img-couple" src="/how-couple.webp" alt="Real connections on Baatcheet" width="1536" height="1024" loading="lazy" decoding="async" />
                <div className="hiw-sticky hiw-sticky-3">Dost banao<br />real ones ❤️</div>
              </div>
            </div>
          </div>
        </section>

        {/* ── WHY SECTION ── */}
        <section className="hiw-why">
          <div className="shell">
            <div className="hiw-why-head reveal">
              <div className="kicker"><span className="kicker-line" /> Kyun Baatcheet?</div>
              <h2 className="h">Log Yahan Aate Hain<br /><span className="stamp">Dil Khol Ke</span></h2>
              <p className="lead">Real reasons, real users — unhi ki zabaan mein.</p>
            </div>

            <div className="hiw-why-layout reveal">
              {/* Left cards */}
              <div className="hiw-why-col">
                <div className="hiw-wcard hiw-wc-cyan">
                  <span className="hiw-wc-ico">🌙</span>
                  <p>"2 baje neend nahi aati — yahan koi na koi jaag raha hota hai."</p>
                </div>
                <div className="hiw-wcard hiw-wc-rose">
                  <span className="hiw-wc-ico">😤</span>
                  <p>"Exam stress mein sirf Baatcheet ne suna — bina judge kiye."</p>
                </div>
                <div className="hiw-wcard hiw-wc-paper">
                  <span className="hiw-wc-ico">🎙️</span>
                  <p>"Type karna boring lagta hai — yahan bas bol do!"</p>
                </div>
              </div>

              {/* Center app image */}
              <div className="hiw-why-center">
                <div className="hiw-app-frame">
                  <img src="/how-chat-app.webp" alt="Baatcheet chat and call app interface" width="1536" height="1024" loading="lazy" decoding="async" />
                  <div className="hiw-app-tag">
                    <span className="cf-dot" /> Live · 2.3k vibing
                  </div>
                </div>
              </div>

              {/* Right cards */}
              <div className="hiw-why-col">
                <div className="hiw-wcard hiw-wc-gold">
                  <span className="hiw-wc-ico">🤝</span>
                  <p>"Naye shahar mein Baatcheet se dost mile — real waale."</p>
                </div>
                <div className="hiw-wcard hiw-wc-teal">
                  <span className="hiw-wc-ico">🏠</span>
                  <p>"Apna feel hai yaar — Made in Bharat, apni language mein."</p>
                </div>
                <div className="hiw-wcard hiw-wc-ink">
                  <span className="hiw-wc-ico">🔥</span>
                  <p>"Dil ki baat, bina judgment ke — Baatcheet pe possible hai."</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section className="block hiw-faq shell">
          <div className="reveal">
            <div className="kicker"><span className="kicker-line" /> Sawal Jawab</div>
            <h2 className="h">Frequently Asked <span className="stamp">Questions</span></h2>
          </div>
          <div className="hiw-faq-list reveal">
            {faqs.map((f, i) => (
              <details className="hiw-faq-item" key={i}>
                <summary>{f.q}</summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* ── STATS ── */}
        <section className="block">
          <div className="shell">
            <div className="reveal">
              <div className="kicker"><span className="kicker-line" /> Numbers Bhi Bolte Hain</div>
              <h2 className="h">Baatcheet ki <span className="hi">धमाकेदार</span> Journey.</h2>
            </div>
            <div className="tickets reveal">
              <div className="tk"><span className="corner">★ 01</span><div className="num">1.2M+</div><div className="lbl">Active monthly users</div></div>
              <div className="tk"><span className="corner">★ 02</span><div className="num">42M</div><div className="lbl">Conversations sparked</div></div>
              <div className="tk"><span className="corner">★ 03</span><div className="num">28</div><div className="lbl">Indian states covered</div></div>
              <div className="tk"><span className="corner">★ 04</span><div className="num">8s</div><div className="lbl">Avg. match time</div></div>
            </div>
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="hiw-cta shell">
          <div className="hiw-cta-card reveal">
            <div className="hiw-cta-left">
              <div className="kicker"><span className="kicker-line" /> Abhi Shuru Karo</div>
              <h2 className="hiw-cta-h">Ready to<br /><span className="hiw-h-stamp">Vibe?</span></h2>
              <p className="lead" style={{ color: 'rgba(255,255,255,.75)' }}>
                Free download. Instant match. Asli baat. Abhi try karo —
                kyunki teri tribe wait kar rahi hai.
              </p>
              <div className="app-row">
                <a
                  className="app-btn hiw-app-btn"
                  href={playStoreUrl('how_it_works_cta')}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent('play_store_click', { location: 'how_it_works_cta' })}
                >
                  <svg width="20" height="22" viewBox="0 0 512 512" aria-hidden="true"><path fill="#4285F4" d="M48,59.49v393a4.33,4.33,0,0,0,7.37,3.07L260,256,55.37,56.42A4.33,4.33,0,0,0,48,59.49Z"/><path fill="#34A853" d="M345.8,174,89.17,32.64l-.07,0c-6.81-3.64-13.36.55-13.36,7.71L260,256Z"/><path fill="#EA4335" d="M89.1,479.36c0,7.16,6.55,11.35,13.36,7.71L345.8,338,260,256Z"/><path fill="#FBBC04" d="M447.34,230.13l-82.08-45.79L260,256l105.26,71.66,82.08-45.79a20.41,20.41,0,0,0,0-51.74Z"/></svg>
                  <div>
                    <div className="top">GET IT ON</div>
                    <div className="bot">Google Play</div>
                  </div>
                </a>
                <a className="app-btn" href="#" style={{ background: '#fff', color: 'var(--ink)' }}>
                  <svg width="17" height="20" viewBox="0 0 814 1000" fill="var(--ink)" aria-hidden="true"><path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-57.8-155.5-127.4C46 790.7 0 663 0 541.8c0-194.3 126.4-297.5 250.8-297.5 66.1 0 121.2 43.4 162.7 43.4 39.5 0 101.1-46 176.3-46 28.5 0 130.9 2.6 198.3 99.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z"/></svg>
                  <div>
                    <div className="top" style={{ color: 'var(--ink-mute)' }}>DOWNLOAD ON</div>
                    <div className="bot">App Store</div>
                  </div>
                </a>
              </div>
            </div>
            <div className="hiw-cta-right">
              <img src="/how-appstore.webp" alt="Baatcheet on Play Store" width="1086" height="1448" loading="lazy" decoding="async" />
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </>
  );
}

export default HowItWorks;
